import {FC, useEffect, useMemo} from "react";
import {useWarehouseLocks} from "../../warehouse/useWarehouseLocks";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {selectCalendar, selectCurrentEmployees, selectEmployeesWorkhours, selectEvents, selectWarehouseLocks,} from "../../../features/AppDataSlice";
import {
    deselectAllEmployees,
    deselectEmployee,
    selectCalendarData,
    selectCurrentEmployee, selectDepartment, selectDepartmentName,
    selectEmployee, selectEmployeeCalendarData,
    selectSelectedEmployees,
    setEditCalendarEvent, setEmployeeCalendarData,
    setReportCalendarEvent
} from "../../../features/AppStateSlice";
import {RRCalendarDateDataType} from "../../types/RRCalendarDataType";
import {RREmployeeType} from "../../types/RREmployeeType";
import {Maybe} from "../../types/Maybe";
import {RREventType} from "../../types/RREventType";
import {RRCalendarDayType} from "../../types/RRCalendarDayType";
import CalendarNoEventsCellContent from "./CalendarNoEventsCellContent";
import CalendarCellContent from "./CalendarCellContent";
import CalendarEmptyCellContent from "./CalendarEmptyCellContent";
import {CancelIconButton, CheckIconButton} from "../../components/IconButtons";
import {Alert, Col, Form, Row, Spinner} from "react-bootstrap";
import {
    RRDEPARTMENT_MONTAGE,
    RRDEPARTMENT_MONTAGE_LABEL,
    RRDEPARTMENT_WAREHOUSE,
    RRDEPARTMENT_WAREHOUSE_LABEL
} from "../../types/RRDepartmentType";
import {dateToString, dateWithZeroTime, minutesToHoursAsString} from "../../tools";

interface Props {
    nameFilter: string;
}

const CalendarBody: FC<Props> = (props) => {
    const department = useAppSelector(selectDepartment);
    const departmentName = useAppSelector(selectDepartmentName);
    const dispatch = useAppDispatch();
    const employees = useAppSelector(selectCurrentEmployees);
    const employeesWorkhours = useAppSelector(selectEmployeesWorkhours);
    const selectedEmployees = useAppSelector(selectSelectedEmployees);
    const allCalendarData = useAppSelector(selectCalendarData);
    const calendarData = useAppSelector(selectEmployeeCalendarData);
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const warehouseLocks = useAppSelector(selectWarehouseLocks);
    const warehouseStatus = useWarehouseLocks();
    const employeeLoading = useAppSelector(state => state.data.employees.loading);
    const employeeError = useAppSelector(state => state.data.employees.error);
    const calendar = useAppSelector(selectCalendar);
    const events = useAppSelector(selectEvents);
    const loading = employeeLoading || calendar.loading || events.loading || !department;
    const loadingError = employeeError || calendar.error || events.error;

    useEffect(() => {
        if(currentEmployee && allCalendarData) {
            dispatch(setEmployeeCalendarData(currentEmployee));
        }
    }, [currentEmployee, allCalendarData]);

    const filteredEmployees = useMemo(() => {
        if (!props.nameFilter) {
            return employees;
        }

        const _filter = props.nameFilter.toLocaleLowerCase();
        return employees?.filter(e => e &&
            (
                department === "catering" && e.isCatering
                || department === "montage" && e.isMontage
                || department === "store" && e.isStore
            )
            && e.name.toLocaleLowerCase().startsWith(_filter))
    }, [employees, props.nameFilter, department]);

    function editCell(employee: RREmployeeType, calendarDay: RRCalendarDateDataType, event: Maybe<RREventType>, item: Maybe<RRCalendarDayType>) {
        if (warehouseStatus(item?.eventDayStart || calendarDay.date, item?.eventId || event?.id || "", item?.department || department).blocked) return;
        dispatch(setEditCalendarEvent({
            employee: employee, calendarDay: calendarDay, event: event, item: item,
            isReadOnly: !(currentEmployee?.isAdmin || currentEmployee?.isSuperAdmin || checkIsCurrentUserIsSupervisor(calendarDay.date))
        }));
    }

    function reportCell(employee: RREmployeeType, calendarDay: RRCalendarDateDataType, event: Maybe<RREventType>, item: Maybe<RRCalendarDayType>) {
        if (warehouseStatus(item?.eventDayStart || calendarDay.date, item?.eventId || event?.id || "", item?.department || department).blocked) return;
        dispatch(setReportCalendarEvent({
            employee: employee, calendarDay: calendarDay, event: event, item: item,
            isReadOnly: !(currentEmployee?.isAdmin || currentEmployee?.isSuperAdmin || checkIsCurrentUserIsSupervisor(calendarDay.date))
        }));
    }

    function checkIsCurrentUserIsSupervisor(forDate: string) {
        let isSupervisor = false;
        calendarData
            .filter(value => value.date === forDate)
            .forEach(
                day => {
                    day.items.forEach(
                        item => {
                            if (item.employeeId === currentEmployee?.id &&
                                (item.isSupervisor || item.isWarehouse)) {
                                const event = day.events.find(
                                    e => e.id === item.eventId
                                )
                                isSupervisor = !!event;
                            }
                        }
                    )
                }
            )
        return isSupervisor;
    }

    const rrDepartments = [RRDEPARTMENT_MONTAGE];
    // const rrDepartments = department === "catering" ? [RRDEPARTMENT_MONTAGE] : [RRDEPARTMENT_MONTAGE, RRDEPARTMENT_WAREHOUSE];
    const rows = useMemo(() => {
        const result: any = [];
        rrDepartments.forEach(
            dep => {
                result.push(<tr key={`employees_header_${dep}`}>
                    <th className={"bg-light"}>
                        <div className={"lh-2"}> Служители&nbsp;
                            {dep === RRDEPARTMENT_MONTAGE ? departmentName.toUpperCase() : RRDEPARTMENT_WAREHOUSE_LABEL}:
                        </div>
                    </th>
                    <th className={"bg-light"}>
                        {
                            selectedEmployees.length ?
                                <CancelIconButton small onClick={() => {
                                    dispatch(deselectAllEmployees())
                                }}/> :
                                <CheckIconButton small onClick={() => {
                                    dispatch(selectEmployee(
                                        filteredEmployees.filter(e => {
                                            return (e.isEmployee) && (e.department === dep) &&
                                                ((department === "catering" && e.isCatering) || (department === "montage" && e.isMontage) || (department === "store" && e.isStore))
                                        }).map(e => e.id!)
                                    ))
                                }}/>

                        }
                    </th>
                    <th colSpan={1000}/>
                </tr>);
                result.push(filteredEmployees.filter(e => {
                    return (e.isEmployee) && (e.department === dep) &&
                        ((department === "catering" && e.isCatering) || (department === "montage" && e.isMontage) || (department === "store" && e.isStore))
                }).map(
                    employee => {
                        const minutes = employeesWorkhours.get(employee.id!)?.workHours || 0;
                        const workhours = minutesToHoursAsString(minutes);

                        return <tr key={employee.id}>
                            <th>
                                <Row>
                                    <Col className={"text-nowrap"}>{employee.name}</Col>
                                    {
                                        (currentEmployee?.isAdmin || currentEmployee?.id === employee.id) &&
                                        <Col xs={"auto"} className={"fw-bold text-end"}>{workhours}</Col>
                                    }
                                </Row>
                            </th>
                            <td className={"w-30px text-center"}>
                                <Form.Check checked={selectedEmployees.includes(employee.id!)}
                                            onChange={event => {
                                                if (event.target.checked) dispatch(selectEmployee(employee.id!))
                                                else dispatch(deselectEmployee(employee.id!))
                                            }}/>
                            </td>
                            {
                                calendarData.map(
                                    (value, idx) => {
                                        const offDay = value.items.find(
                                            item => item.employeeId === employee.id && item.dayType !== "WORKDAY"
                                        );

                                        const activeEvents = value.events;
                                        if (activeEvents.length > 0) {
                                            return activeEvents.map(
                                                (value1, idx) => {
                                                    const baseCellKey = `${employee.id}_${value.date}_${value1.id}`;
                                                    const employeeItem = value.items.find(
                                                        value => value.employeeId === currentEmployee?.id && value.eventId === value1.id
                                                    );

                                                    const item = value.items.find(
                                                        value => value.employeeId === employee.id && value.eventId === value1.id
                                                    );

                                                    const isSupervisor = checkIsCurrentUserIsSupervisor(value.date);
                                                    let canView =
                                                        currentEmployee?.isAdmin
                                                        || ((employeeItem || item) && employeeItem?.eventId === item?.eventId)
                                                        || employeeItem?.isSupervisor
                                                        || employeeItem?.isWarehouse
                                                        || isSupervisor;

                                                    let canEdit = (currentEmployee?.isAdmin
                                                            || employeeItem?.isSupervisor
                                                            || employeeItem?.isWarehouse
                                                            || item?.employeeId === currentEmployee?.id
                                                            || isSupervisor)
                                                        && (value1.eventStatus !== "ЗАВЪРШЕНО"
                                                            || !item?.isConfirmed)
                                                        && (value1.eventStatus !== "НОВО")
                                                    ;

                                                    let canReport = canEdit && (currentEmployee?.isAdmin
                                                        || currentEmployee?.id === employee.id
                                                        || employeeItem?.isSupervisor
                                                        || employeeItem?.isWarehouse
                                                        || false);
                                                    if (currentEmployee?.id === employee.id && value1.eventStatus !== "ЗАВЪРШЕНО") {
                                                        canReport = true;
                                                    }
                                                    if (value1.eventStatus === "ЗАВЪРШЕНО") {
                                                        canEdit = false;
                                                        canReport = false;
                                                    }
                                                    if (value1.id === "WAREHOUSE") {
                                                        if (!warehouseStatus(value.date).blocked) {
                                                        } else {
                                                            canEdit = false;
                                                            canReport = false;
                                                        }
                                                    }

                                                    if (canView) {
                                                        return offDay ? <CalendarNoEventsCellContent key={`${baseCellKey}_offday`} colIndex={idx}
                                                                                                     onClick={canEdit ?
                                                                                                         (item) => editCell(employee, value, null, item)
                                                                                                         : undefined}
                                                                                                     offDay={offDay}/>
                                                            :
                                                            <CalendarCellContent key={baseCellKey} values={value}
                                                                                 montageSign=""
                                                                                 event={value1} item={item}
                                                                                 employee={employee} colIndex={idx}
                                                                                 offDay={offDay}
                                                                                 onClickStart={canEdit ? (item) => editCell(employee, value, value1, item)
                                                                                     : undefined}
                                                                                 onClickEnd={canReport ? (item) => reportCell(employee, value, value1, item)
                                                                                     : undefined}
                                                            />
                                                    } else return <CalendarEmptyCellContent key={`${baseCellKey}_empty`} colIndex={idx}
                                                                                            event={value1}/>
                                                }
                                            )
                                        }

                                        return <CalendarNoEventsCellContent key={`${employee.id}_${value.date}_noevents`} colIndex={idx}
                                                                            onClick={(item) => editCell(employee, value, null, item)}
                                                                            offDay={offDay}/>
                                    }
                                )
                            }
                        </tr>

                    }
                ));
            });

        return result;
    }, [calendarData, filteredEmployees, employeesWorkhours, selectedEmployees, warehouseLocks, warehouseStatus, currentEmployee, department]);

    return (
        <tbody>
        {loadingError ? <tr><td colSpan={1000}>
            <Alert variant="danger" role="alert" className="mb-0">
                Неуспешно зареждане на календара. Проверете връзката и презаредете страницата.
                <div className="small">{loadingError}</div>
            </Alert>
        </td></tr> : loading ? <tr><td colSpan={1000}>
            <div role="status" className="p-3">
                <Spinner size="sm" className="me-2"/>
                Зареждане на служителите и дейностите…
            </div>
        </td></tr> : rows}
        </tbody>
    )
}

export default CalendarBody;
